/**
 * Yarnly Crochet — glossary.js
 *
 * <yarnly-glossary> (sections/glossary.liquid) adds a live search to the
 * crochet terms table.
 *
 * Markup contract
 *   <yarnly-glossary data-status-one data-status-other="[count] …" data-status-none>
 *     [data-glossary-search] hidden   the search box, revealed by this script
 *       input[data-glossary-input]
 *     tr[data-glossary-row][data-search="lower-cased row text"]
 *     [data-glossary-empty] hidden    shown when nothing matches
 *     [data-glossary-status]          polite live region for the match count
 *   </yarnly-glossary>
 *
 * Typing filters the rows at once (a row matches when its data-search text
 * contains the query, case-insensitive) and announces the count after a short
 * pause so a screen reader is not interrupted on every keystroke. Escape
 * clears the search. Without JavaScript the search stays hidden and every term
 * shows. Strings arrive pre-translated in the data attributes.
 *
 * Plain ES2020 IIFE, safe to evaluate twice (the define is guarded).
 */
(function () {
  'use strict';

  const S = window.Yarnly;
  if (!S) return;

  const ANNOUNCE_DELAY = 250;

  function format(template, values) {
    const fn = S.utils && S.utils.formatTemplate;
    if (typeof fn === 'function') return fn(template, values);
    return String(template || '').replace(/\[(\w+)\]/g, (match, name) =>
      Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : match
    );
  }

  if (!customElements.get('yarnly-glossary')) {
    class YarnlyGlossary extends HTMLElement {
      constructor() {
        super();
        this.onInput = this.onInput.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.announceTimer = null;
        this.writeTimer = null;
      }

      connectedCallback() {
        this.search = this.querySelector('[data-glossary-search]');
        this.input = this.querySelector('[data-glossary-input]');
        if (!this.search || !this.input) return;
        this.search.hidden = false;
        this.input.addEventListener('input', this.onInput);
        this.input.addEventListener('keydown', this.onKeyDown);
        // A value the browser restored (back/forward cache) filters straight away.
        if (this.input.value) this.filter(false);
      }

      disconnectedCallback() {
        clearTimeout(this.announceTimer);
        clearTimeout(this.writeTimer);
        if (!this.input) return;
        this.input.removeEventListener('input', this.onInput);
        this.input.removeEventListener('keydown', this.onKeyDown);
      }

      onInput() {
        this.filter(true);
      }

      onKeyDown(event) {
        if ((event.key !== 'Escape' && event.key !== 'Esc') || !this.input.value) return;
        event.preventDefault();
        this.input.value = '';
        this.filter(true);
      }

      filter(announce) {
        const query = (this.input.value || '').trim().toLowerCase();
        let shown = 0;
        this.querySelectorAll('[data-glossary-row]').forEach((row) => {
          const match = !query || (row.getAttribute('data-search') || '').indexOf(query) !== -1;
          row.hidden = !match;
          if (match) shown += 1;
        });
        const empty = this.querySelector('[data-glossary-empty]');
        if (empty) empty.hidden = shown > 0;
        if (announce) this.scheduleAnnounce(query ? this.statusText(shown) : '');
      }

      statusText(count) {
        if (count === 0) return this.getAttribute('data-status-none') || '';
        if (count === 1) return this.getAttribute('data-status-one') || '';
        return format(this.getAttribute('data-status-other') || '', { count });
      }

      // Debounced, then cleared and rewritten so an identical message is read again.
      scheduleAnnounce(text) {
        const status = this.querySelector('[data-glossary-status]');
        if (!status) return;
        clearTimeout(this.announceTimer);
        clearTimeout(this.writeTimer);
        this.announceTimer = setTimeout(() => {
          status.textContent = '';
          if (!text) return;
          this.writeTimer = setTimeout(() => {
            status.textContent = text;
          }, 50);
        }, ANNOUNCE_DELAY);
      }
    }

    customElements.define('yarnly-glossary', YarnlyGlossary);
  }
})();
